import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { listProducts } from '../services/data'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, Button, FilterBar } from '../components/shared/ui'
import { currency, productCode } from '../lib/format'
import { useServerList } from '../lib/list'
import ProductForm from '../components/ProductForm'
import type { Product } from '../types'

const PAGE_SIZE = 25

export default function Products() {
  const navigate = useNavigate()
  const [showNew, setShowNew] = useState(false)
  const { rows, count, page, search, sort, loading, setPage, setSearch, setFilters, toggleSort } =
    useServerList<Product>(listProducts, { pageSize: PAGE_SIZE, defaultSort: { column: 'name', dir: 'asc' } })

  return (
    <div>
      <PageHeader
        title="Products"
        subtitle={`${count.toLocaleString()} items`}
        actions={<Button onClick={() => setShowNew(true)}><Plus size={16} /> New Product</Button>}
      />
      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, SKU, scan…" />
          <FilterBar
            onChange={setFilters}
            fields={[
              { key: 'type', label: 'Type', column: 'inventory_type', type: 'text', placeholder: 'e.g. Tree' },
              { key: 'active', label: 'Active', column: 'is_active', type: 'boolean' },
              { key: 'depleted', label: 'Depleted', column: 'is_depleted', type: 'boolean' },
            ]}
          />
        </div>
        {loading ? (
          <Spinner label="Loading products…" />
        ) : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              onRowClick={p => navigate(`/products/${p.id}`)}
              columns={[
                { key: 'name', header: 'Name', render: p => <span className="font-medium text-gray-900">{p.name}</span>, sortKey: 'name' },
                { key: 'sku', header: 'Code / Scan', render: p => productCode(p), sortKey: 'sku' },
                { key: 'inventory_type', header: 'Type', render: p => p.inventory_type || '—', sortKey: 'inventory_type' },
                { key: 'qty_remaining', header: 'On Hand', render: p => p.qty_remaining ?? p.qty_on_hand ?? 0, className: 'text-right', sortKey: 'qty_remaining' },
                { key: 'cost_price', header: 'Cost', render: p => currency(p.cost_price), className: 'text-right', sortKey: 'cost_price' },
                { key: 'retail_price', header: 'Retail', render: p => currency(p.retail_price), className: 'text-right', sortKey: 'retail_price' },
                { key: 'status', header: 'Status', render: p => p.is_depleted
                  ? <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs">Depleted</span>
                  : <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">Active</span> },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>

      {showNew && (
        <ProductForm mode="new" onClose={() => setShowNew(false)} onSaved={p => { setShowNew(false); navigate(`/products/${p.id}`) }} />
      )}
    </div>
  )
}
